import { z } from 'zod';
import { CUSTOM_FIELD_ENTITIES, CUSTOM_FIELD_TYPES, USER_ROLES } from '../enums.js';
import { EntityMetaSchema, zEnum, zNonEmpty } from './common.js';

/**
 * CustomFieldDefinition — tenant-configurable extra fields on core entities (Phase II P2-M1).
 * Addresses the top HCP complaint ("no custom fields"). Values are stored on each entity's
 * `customFields` map keyed by the definition's `key`; definitions drive UI + validation.
 */
export const CustomFieldDefinitionSchema = EntityMetaSchema.extend({
  entityType: zEnum(CUSTOM_FIELD_ENTITIES),
  key: z.string().trim().regex(/^[a-z][a-z0-9_]{0,48}$/, 'Use lowercase letters, numbers, underscores'),
  label: zNonEmpty.max(80),
  type: zEnum(CUSTOM_FIELD_TYPES),
  options: z.array(z.string().max(80)).default([]), // for select / multiselect
  required: z.boolean().default(false),
  searchable: z.boolean().default(false),
  roleVisibility: z.array(zEnum(USER_ROLES)).default([]), // empty = visible to all roles
  order: z.number().int().default(0),
  active: z.boolean().default(true),
});
export type CustomFieldDefinition = z.infer<typeof CustomFieldDefinitionSchema>;

export const CreateCustomFieldSchema = z.object({
  entityType: zEnum(CUSTOM_FIELD_ENTITIES),
  key: z.string().trim().regex(/^[a-z][a-z0-9_]{0,48}$/, 'Use lowercase letters, numbers, underscores'),
  label: zNonEmpty.max(80),
  type: zEnum(CUSTOM_FIELD_TYPES),
  options: z.array(z.string().max(80)).optional(),
  required: z.boolean().optional(),
  searchable: z.boolean().optional(),
  roleVisibility: z.array(zEnum(USER_ROLES)).optional(),
  order: z.number().int().optional(),
});
export type CreateCustomFieldInput = z.infer<typeof CreateCustomFieldSchema>;

export const UpdateCustomFieldSchema = CreateCustomFieldSchema.partial().omit({ entityType: true, key: true }).extend({
  active: z.boolean().optional(),
});
export type UpdateCustomFieldInput = z.infer<typeof UpdateCustomFieldSchema>;

/**
 * Validate a bag of custom-field values against a set of definitions. Returns per-key errors.
 * Enforces required + type + select membership. Unknown keys are dropped (no mass assignment).
 */
export function validateCustomFieldValues(
  defs: readonly CustomFieldDefinition[],
  values: Record<string, string> = {},
): { clean: Record<string, string>; errors: Record<string, string> } {
  const clean: Record<string, string> = {};
  const errors: Record<string, string> = {};
  const byKey = new Map(defs.filter((d) => d.active).map((d) => [d.key, d]));
  for (const def of byKey.values()) {
    const raw = values[def.key];
    if (raw === undefined || raw === '') {
      if (def.required) errors[def.key] = `${def.label} is required`;
      continue;
    }
    switch (def.type) {
      case 'number':
        if (Number.isNaN(Number(raw))) errors[def.key] = `${def.label} must be a number`;
        else clean[def.key] = raw;
        break;
      case 'checkbox':
        clean[def.key] = raw === 'true' ? 'true' : 'false';
        break;
      case 'select':
        if (def.options.length && !def.options.includes(raw)) errors[def.key] = `${def.label}: invalid option`;
        else clean[def.key] = raw;
        break;
      case 'multiselect': {
        const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
        if (def.options.length && parts.some((p) => !def.options.includes(p))) errors[def.key] = `${def.label}: invalid option`;
        else clean[def.key] = parts.join(',');
        break;
      }
      default:
        clean[def.key] = raw;
    }
  }
  return { clean, errors };
}
